/**
 * 从动态菜单树中解析当前页面的 Toolbar 按钮（纯函数，不依赖 router/api）
 */

const buttonPathCache = {
  source: null,
  map: null
};

/**
 * 统一路由 path 便于与菜单 Code/path 对齐。最后修改：2026-08-29
 */
export function normalizeRoutePath(path) {
  if (path == null || path === '') return '';
  let normalized = String(path).trim().toLowerCase();
  if (!normalized) return '';
  if (normalized !== '/' && !normalized.startsWith('/')) {
    normalized = `/${normalized}`;
  }
  normalized = normalized.replace(/\/+$/, '');
  return normalized;
}

function resolveNodePath(node) {
  if (!node) return '';
  return normalizeRoutePath(node.path || node.Code || node.code || '');
}

function normalizeToolbarButton(item) {
  if (!item) return item;
  return {
    ...item,
    name: item.name || item.Name || '',
    Func: item.Func || item.func || '',
    IsHide: item.IsHide != null ? item.IsHide : (item.isHide != null ? item.isHide : false),
    IsButton: item.IsButton != null ? item.IsButton : (item.isButton != null ? item.isButton : true),
  };
}

function isToolbarButtonNode(item) {
  if (!item) return false;
  if (item.IsButton === true || item.isButton === true) return true;
  const func = String(item.Func || item.func || '').trim();
  const path = resolveNodePath(item);
  return !!func && !path;
}

function buildButtonPathMap(routers) {
  const map = new Map();
  const walk = (nodes) => {
    (nodes || []).forEach((node) => {
      const nodePath = resolveNodePath(node);
      if (nodePath && !node.IsButton) {
        map.set(nodePath, node.children || []);
      }
      if (node.children && node.children.length) {
        walk(node.children);
      }
    });
  };
  walk(routers);
  return map;
}

function findPageChildrenByWalk(routers, routePath) {
  const target = normalizeRoutePath(routePath);
  if (!target) return null;
  let found = null;
  const walk = (nodes) => {
    (nodes || []).forEach((node) => {
      if (found != null) return;
      if (node.IsButton) return;
      const nodePath = resolveNodePath(node);
      if (nodePath && nodePath === target) {
        found = node.children || [];
        return;
      }
      if (node.children && node.children.length) {
        walk(node.children);
      }
    });
  };
  walk(routers);
  return found;
}

function resolveToolbarButtons(children) {
  return (children || [])
    .filter((item) => isToolbarButtonNode(item))
    .map((item) => normalizeToolbarButton(item));
}

export function getButtonList(routePath, routers) {
  if (!routers || !routers.length || !routePath) return [];
  if (buttonPathCache.source !== routers) {
    buttonPathCache.source = routers;
    buttonPathCache.map = buildButtonPathMap(routers);
  }
  const normalizedPath = normalizeRoutePath(routePath);
  const direct = buttonPathCache.map.get(normalizedPath);
  const children = direct != null ? direct : findPageChildrenByWalk(routers, routePath);
  if (children == null) return [];
  return resolveToolbarButtons(children);
}

/** 当前用户动态菜单中是否包含指定页面路由（含 IsHide 隐藏页） */
export function hasRouteAccess(routePath, routers) {
  if (!routers || !routers.length || !routePath) return false;
  const target = normalizeRoutePath(routePath);
  let found = false;
  const walk = (nodes) => {
    (nodes || []).forEach((node) => {
      if (!node.IsButton && resolveNodePath(node) === target) {
        found = true;
      }
      if (node.children && node.children.length) {
        walk(node.children);
      }
    });
  };
  walk(routers);
  return found;
}

let cachedButtonRouters = null;

/** 读取并缓存 localStorage 中的菜单树 */
export function getCachedButtonRouters() {
  if (cachedButtonRouters) return cachedButtonRouters;
  if (!window.localStorage.router) {
    cachedButtonRouters = [];
    return cachedButtonRouters;
  }
  try {
    cachedButtonRouters = JSON.parse(window.localStorage.router);
  } catch (e) {
    cachedButtonRouters = [];
  }
  return cachedButtonRouters;
}

/** 菜单变更后调用（如重新登录） */
export function invalidateButtonRouterCache() {
  cachedButtonRouters = null;
  buttonPathCache.source = null;
  buttonPathCache.map = null;
}
